import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight, ArrowLeft, RotateCcw, TreeDeciduous, UserPlus,
  Users, Shield, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle2, Lightbulb, Target, BookOpen, HelpCircle,
  ShieldAlert, Info, Sparkles, Lock, Compass, Bookmark, Send,
} from 'lucide-react';
import {
  trackPremiumTeaserAngezeigt,
  trackPremiumCtaKlick,
  trackPremiumStolpersteinGeoeffnet,
  trackPremiumWpwGeoeffnet,
  trackPremiumQuellenGeoeffnet,
  trackStep3CtaClick,
  trackPremiumInteresseRegistriert,
} from '@/lib/tracking';
import { fallanalyseAPI } from '@/lib/api';

// ============================================================
// QUELLEN-BADGE (shared)
// ============================================================
function QuellenBadge({ tag, t }) {
  const config = {
    rechtlich: { label: t('fa.tagRechtlich'), cls: 'bg-red-50 text-red-700 border-red-200' },
    nachweisbezogen: { label: t('fa.tagNachweisbezogen'), cls: 'bg-red-50 text-red-700 border-red-200' },
    foerderrelevant: { label: t('fa.tagFoerderrelevant'), cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    fachlich: { label: t('fa.tagFachlich'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    oekologisch: { label: t('fa.tagOekologisch'), cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    praxisnah: { label: t('fa.tagPraxisnah'), cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  };
  const c = config[tag] || config.praxisnah;
  return (
    <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${c.cls}`} data-testid={`quellen-badge-${tag}`}>
      {c.label}
    </span>
  );
}

// ============================================================
// STOLPERSTEIN (Premium)
// ============================================================
function StolpersteinItem({ stein, t }) {
  const [offen, setOffen] = useState(false);
  const katIcons = { gesetzlich: ShieldAlert, foerderbezogen: BookOpen, betriebswirtschaftlich: Target, oekologisch: AlertTriangle, organisatorisch: Users };
  const Icon = katIcons[stein.kategorie] || Info;

  return (
    <div className="border border-warm-200 rounded-lg p-3 bg-white" data-testid={`stolperstein-${stein.kategorie}`}>
      <div className="flex items-start gap-2.5">
        <Icon className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 leading-relaxed">{stein.text}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <QuellenBadge tag={stein.quellen_tag} t={t} />
            {stein.bundesland_spezifisch && <span className="text-[11px] text-muted-foreground">{t('fa.landesspezifisch')}</span>}
          </div>
          <button
            onClick={() => { setOffen(!offen); if (!offen) trackPremiumStolpersteinGeoeffnet(stein.kategorie); }}
            className="text-xs text-primary/70 hover:text-primary mt-1.5 flex items-center gap-1 transition-colors"
            data-testid={`stolperstein-detail-toggle-${stein.kategorie}`}
          >
            {offen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {offen ? t('fa.weniger') : t('fa.warum')} · {stein.quelle_name}
          </button>
          {offen && (
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed pl-0.5 border-l-2 border-primary/10 ml-0.5">
              {stein.quelle_detail}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// WAS PASSIERT WENN (Premium)
// ============================================================
function WasPassiertWennItem({ item, t }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className="border border-warm-200 rounded-lg p-3 bg-white" data-testid={`wpw-item`}>
      <button
        onClick={() => { setOffen(!offen); if (!offen) trackPremiumWpwGeoeffnet(item.szenario); }}
        className="flex items-start gap-2.5 w-full text-left"
      >
        <HelpCircle className="h-4 w-4 text-primary/60 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{item.szenario}</p>
          {offen && (
            <div className="mt-2">
              <p className="text-sm text-gray-700 leading-relaxed">{item.konsequenz}</p>
              <div className="mt-1.5"><QuellenBadge tag={item.quellen_tag} t={t} /></div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed pl-0.5 border-l-2 border-primary/10 ml-0.5">{item.quelle_detail}</p>
            </div>
          )}
        </div>
        {offen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
      </button>
    </div>
  );
}


// ============================================================
// PREMIUM-TEASER (Basis-Tier: kein entscheidung-Objekt)
// ============================================================
function PremiumTeaser({ ansicht, onRegister, onProjektEntwurf, onNeustart, onZurueck, onFreigabe, t }) {
  const { vorentscheidung } = ansicht;
  const vorschau = vorentscheidung?.premium_vorschau;
  const falltyp = ansicht?.erkannt?.erkannte_merkmale?.find(m => m.label === 'Massnahme' || m.label === 'Maßnahme')?.wert || 'unbekannt';
  const zielgruppe = ansicht?.zielgruppe?.key || 'waldbesitzer';
  const [interesseGesendet, setInteresseGesendet] = useState(false);

  const KATEGORIE_CONFIG = {
    betriebswirtschaftlich: { icon: Compass, cls: 'text-blue-600 bg-blue-50 border-blue-200' },
    organisatorisch: { icon: Users, cls: 'text-slate-600 bg-slate-50 border-slate-200' },
    oekologisch: { icon: AlertTriangle, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    gesetzlich: { icon: ShieldAlert, cls: 'text-red-600 bg-red-50 border-red-200' },
    foerderbezogen: { icon: BookOpen, cls: 'text-blue-600 bg-blue-50 border-blue-200' },
  };

  useEffect(() => {
    trackPremiumTeaserAngezeigt(falltyp, zielgruppe);
  }, [falltyp, zielgruppe]);

  const handlePremiumInteresse = async () => {
    trackPremiumInteresseRegistriert(falltyp, 'premium_teaser');
    trackPremiumCtaKlick(falltyp, zielgruppe);
    try {
      await fallanalyseAPI.premiumInteresse(null, falltyp, 'premium_teaser');
      setInteresseGesendet(true);
    } catch {
      setInteresseGesendet(true);
    }
  };

  return (
    <div data-testid="step-weiter">
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-1">
          {t('fa.vertieftePruefung')}
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('fa.vertiefteBeschreibung')}
        </p>
      </div>

      {vorschau && (
        <>
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('fa.wirdGeprueft')}
          </h2>
          <div className="space-y-2 mb-5" data-testid="premium-pruefpunkte">
            {vorschau.pruefpunkte.map((p) => {
              const cfg = KATEGORIE_CONFIG[p.kategorie] || KATEGORIE_CONFIG.organisatorisch;
              const Icon = cfg.icon;
              return (
                <div key={p.kategorie} className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.cls}`} data-testid={`premium-pruefpunkt-${p.kategorie}`}>
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{p.label}</p>
                    <p className="text-xs opacity-80 mt-0.5">{p.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-2 mb-5">
            {vorschau.extras.map((e, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                <CheckCircle2 className="h-4 w-4 text-primary/60 mt-0.5 shrink-0" />
                <span>{e}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mb-5 p-3 rounded-lg bg-warm-50 border border-warm-200" data-testid="premium-zusammenfassung">
            {vorschau.zusammenfassung}
          </p>
        </>
      )}

      <Card className="border-primary/20 bg-primary/[0.02] mb-5">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <ArrowRight className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground mb-1">
                {t('fa.inVorbereitung')}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('fa.belastbareHilfe')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TEIL B: 49-€-Leistung konkret */}
      <div className="mb-5 bg-warm-50/80 border border-warm-200 rounded-xl p-4" data-testid="leistung-49">
        <p className="text-sm font-semibold text-foreground mb-2">Sie erhalten für 49 €:</p>
        <ul className="space-y-1.5">
          {[
            'Eine klar strukturierte Zusammenfassung Ihres Falls',
            'Eine Einordnung aus mehreren Blickwinkeln (z.\u00A0B. Nutzung, Einschränkungen, Risiken)',
            'Eine Übersicht der wichtigsten offenen Punkte',
            'Konkrete nächste Schritte für Ihre Situation',
            'Ein PDF, das Sie für Gespräche mit Forstamt, Berater, Käufer oder Familie nutzen können',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* TEIL D: Abgrenzung */}
      <p className="text-xs text-muted-foreground leading-relaxed mb-5 px-1" data-testid="abgrenzung-hinweis">
        ForestHub ersetzt keine Fachberatung. Es hilft, Ihren Fall vorher zu ordnen, damit Gespräche mit Forstamt, Berater oder Käufer klarer und schneller werden.
      </p>

      {!interesseGesendet ? (
        <Button
          onClick={handlePremiumInteresse}
          variant="outline"
          className="w-full h-10 text-sm font-medium border-primary/20 hover:bg-primary/5 mb-3"
          data-testid="cta-premium-interesse"
        >
          <Bookmark className="h-4 w-4 mr-2" />
          {t('fa.interesseAnzeigen')}
        </Button>
      ) : (
        <div
          className="w-full h-10 text-sm font-medium mb-3 flex items-center justify-center gap-2 rounded-md bg-primary/5 text-primary border border-primary/15"
          data-testid="premium-interesse-bestaetigt"
        >
          <CheckCircle2 className="h-4 w-4" />
          {t('fa.interesseBestaetigt')}
        </div>
      )}

      <Button
        onClick={() => { trackStep3CtaClick('analyse_speichern_step4', falltyp); (onProjektEntwurf || onRegister)?.(); }}
        className="w-full h-12 text-base font-medium mb-3"
        data-testid="cta-analyse-speichern-step4"
      >
        <ArrowRight className="h-5 w-5 mr-2" />
        {t('fa.analyseSpeichernOffen')}
      </Button>

      {onFreigabe && (
        <Button
          onClick={onFreigabe}
          variant="outline"
          className="w-full h-10 text-sm font-medium border-primary/20 hover:bg-primary/5 mb-5"
          data-testid="cta-freigabe-step4"
        >
          <Send className="h-4 w-4 mr-2" />
          Direkt für Angebote freigeben
        </Button>
      )}

      <div className="flex gap-3">
        {onZurueck && (
          <Button variant="outline" onClick={onZurueck} data-testid="zurueck-btn-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('fa.zurueck')}
          </Button>
        )}
        <Button variant="outline" onClick={onNeustart} className="flex-1" data-testid="neustart-btn">
          <RotateCcw className="h-4 w-4 mr-2" /> {t('fa.neuenFall')}
        </Button>
      </div>
    </div>
  );
}


// ============================================================
// PREMIUM CONTENT (Volle Entscheidungshilfe)
// ============================================================
function PremiumContent({
  entscheidung, onNeustart, onProjektErstellen, isPublic, onRegister,
  onZurueck, onSammelfallBuendeln, onDienstleisterProfil, onProjektEntwurf, t,
}) {
  const [altOffen, setAltOffen] = useState(false);
  const [quellenOffen, setQuellenOffen] = useState(false);

  const {
    empfehlung_satz, empfohlen, warnhinweise, alternativen,
    stolpersteine = [], was_passiert_wenn = [],
    quellen_zusammenfassung = [], mehrwert_text = '',
  } = entscheidung;

  return (
    <>
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-1">
          {t('fa.unsereEmpfehlung')}
        </h1>
        {mehrwert_text && (
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="mehrwert-text">{mehrwert_text}</p>
        )}
      </div>

      <Card className="border-primary/25 shadow-sm mb-5" data-testid="empfehlung-primaer">
        <CardContent className="pt-5 pb-5">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground leading-snug" data-testid="empfehlung-satz">{empfehlung_satz}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{empfohlen.beschreibung}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mb-5" data-testid="empfehlung-gruende">
        <h2 className="text-sm font-semibold text-foreground mb-2.5 flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-primary/70" /> {t('fa.warumEmpfohlen')}
        </h2>
        <ul className="space-y-2">
          {empfohlen.gruende.map((grund, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
              <CheckCircle2 className="h-4 w-4 text-primary/60 mt-0.5 shrink-0" /><span>{grund}</span>
            </li>
          ))}
        </ul>
      </div>

      <Card className="border-warm-200 bg-warm-50/50 mb-5" data-testid="naechster-schritt-card">
        <CardContent className="pt-5 pb-5">
          <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary/70" /> {t('fa.jetztTun')}
          </h2>
          <p className="text-sm font-medium text-foreground mb-1" data-testid="schritt-text">{empfohlen.naechster_schritt}</p>
          <p className="text-sm text-gray-600 leading-relaxed" data-testid="schritt-details">{empfohlen.naechster_schritt_details}</p>
        </CardContent>
      </Card>

      {(empfohlen.voraussetzungen.length > 0 || warnhinweise.length > 0) && (
        <div className="mb-5" data-testid="voraussetzungen">
          <h2 className="text-sm font-semibold text-foreground mb-2.5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> {t('fa.woraufAchten')}
          </h2>
          <ul className="space-y-2">
            {empfohlen.voraussetzungen.map((v, i) => (
              <li key={`v-${i}`} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" /><span>{v}</span>
              </li>
            ))}
            {warnhinweise.map((w, i) => (
              <li key={`w-${i}`} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" /><span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {stolpersteine.length > 0 && (
        <div className="mb-5" data-testid="stolpersteine-section">
          <h2 className="text-sm font-semibold text-foreground mb-2.5 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600" /> {t('fa.stolpersteine')}
          </h2>
          <div className="space-y-2">
            {stolpersteine.map((s, i) => <StolpersteinItem key={i} stein={s} t={t} />)}
          </div>
        </div>
      )}

      {was_passiert_wenn.length > 0 && (
        <div className="mb-5" data-testid="was-passiert-wenn-section">
          <h2 className="text-sm font-semibold text-foreground mb-2.5 flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary/60" /> {t('fa.wasPassiertWenn')}
          </h2>
          <div className="space-y-2">
            {was_passiert_wenn.map((w, i) => <WasPassiertWennItem key={i} item={w} t={t} />)}
          </div>
        </div>
      )}

      {quellen_zusammenfassung.length > 0 && (
        <div className="mb-5" data-testid="quellen-section">
          <button
            onClick={() => { setQuellenOffen(!quellenOffen); if (!quellenOffen) trackPremiumQuellenGeoeffnet(); }}
            className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1.5"
            data-testid="quellen-toggle"
          >
            <BookOpen className="h-3.5 w-3.5" />
            {quellenOffen ? t('fa.quellenAusblenden') : t('fa.quellenAnzeigen')}
            {quellenOffen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {quellenOffen && (
            <div className="mt-2 p-3 rounded-lg border border-warm-200 bg-warm-50/30 space-y-2" data-testid="quellen-details">
              {quellen_zusammenfassung.map((q) => (
                <div key={q.typ} className="text-xs text-gray-600">
                  <span className="font-medium text-foreground">{q.label}</span>
                  <span className="text-muted-foreground"> · {q.anzahl} {q.anzahl === 1 ? t('fa.quelle') : t('fa.quellen')}</span>
                  <p className="text-muted-foreground mt-0.5 pl-2 border-l border-warm-300">{t('fa.zB')} {q.beispiele.join(', ')}</p>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground mt-2 pt-2 border-t border-warm-200">
                {t('fa.quellenDisclaimer')}
              </p>
            </div>
          )}
        </div>
      )}

      {isPublic ? (
        <div className="space-y-3 mb-5">
          {onSammelfallBuendeln && (
            <Button onClick={onSammelfallBuendeln} className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90" data-testid="cta-sammelfall-buendeln">
              <Users className="h-5 w-5 mr-2" /> {t('fa.mitgliederBuendeln')} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
          {onDienstleisterProfil && (
            <Card className="border-accent/20 bg-accent/[0.03]">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">{t('fa.sindDienstleister')}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-2">{t('fa.profilErstellen')}</p>
                    <Button onClick={onDienstleisterProfil} variant="outline" size="sm" className="border-accent/30 text-accent hover:bg-accent/5" data-testid="cta-dienstleister-profil">
                      <Shield className="h-3.5 w-3.5 mr-1.5" /> {t('fa.dienstleisterProfil')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-primary/15 bg-primary/[0.02]">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-semibold text-foreground mb-2">{t('fa.einordnungSichern')}</p>
              <ul className="text-sm text-muted-foreground space-y-1.5 leading-relaxed">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /><span>{t('fa.empfehlungSpeichern1')}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /><span>{t('fa.empfehlungSpeichern2')}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /><span>{t('fa.empfehlungSpeichern3')}</span></li>
              </ul>
            </CardContent>
          </Card>
          <Button onClick={onProjektEntwurf || onRegister} className="w-full h-12 text-base font-medium" data-testid="cta-empfehlung-sichern">
            <TreeDeciduous className="h-5 w-5 mr-2" /> {t('fa.empfehlungSichern')} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="space-y-3 mb-5">
          <Button onClick={onProjektErstellen} className="w-full h-12 text-base font-medium" data-testid="cta-projekt-erstellen">
            <TreeDeciduous className="h-5 w-5 mr-2" /> {t('fa.projektErstellen')} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {alternativen.length > 0 && (
        <div className="mb-5" data-testid="alternativen-section">
          <button onClick={() => setAltOffen(!altOffen)} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full py-2" data-testid="alternativen-toggle">
            {altOffen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {t('fa.andereWege')}
          </button>
          {altOffen && (
            <div className="mt-2 space-y-3">
              {alternativen.map((alt) => (
                <div key={alt.key} className="p-4 rounded-lg border border-warm-200 bg-white" data-testid={`alternative-${alt.key}`}>
                  <p className="text-sm font-semibold text-foreground mb-1">{alt.name}</p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2">{alt.beschreibung}</p>
                  <p className="text-xs text-muted-foreground italic">{t('fa.sinnvoll')} {alt.wann_sinnvoll.charAt(0).toLowerCase() + alt.wann_sinnvoll.slice(1)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        {onZurueck && (
          <Button variant="outline" onClick={onZurueck} data-testid="zurueck-btn-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('fa.zurueck')}
          </Button>
        )}
        <Button variant="outline" onClick={onNeustart} className="flex-1" data-testid="neustart-btn">
          <RotateCcw className="h-4 w-4 mr-2" /> {t('fa.neuenFall')}
        </Button>
      </div>
    </>
  );
}


// ============================================================
// HAUPTKOMPONENTE
// ============================================================
export function StepWeiter({
  ansicht, onNeustart, onProjektErstellen, isPublic, onRegister,
  onZurueck, onSammelfallBuendeln, onDienstleisterProfil, onProjektEntwurf, onFreigabe, t,
}) {
  const { entscheidung, tier } = ansicht;

  if (!entscheidung || tier === 'basis') {
    return (
      <PremiumTeaser
        ansicht={ansicht}
        onRegister={onRegister}
        onProjektEntwurf={onProjektEntwurf}
        onNeustart={onNeustart}
        onZurueck={onZurueck}
        onFreigabe={onFreigabe}
        t={t}
      />
    );
  }

  return (
    <div data-testid="step-weiter">
      <PremiumContent
        entscheidung={entscheidung}
        onNeustart={onNeustart}
        onProjektErstellen={onProjektErstellen}
        isPublic={isPublic}
        onRegister={onRegister}
        onZurueck={onZurueck}
        onSammelfallBuendeln={onSammelfallBuendeln}
        onDienstleisterProfil={onDienstleisterProfil}
        onProjektEntwurf={onProjektEntwurf}
        t={t}
      />
    </div>
  );
}
